import type { ImplementedGhostId } from "../ghosts/GhostDefinitions";
import type { WeaponId } from "../inventory/ItemDefinitions";
import { DEFAULT_DIFFICULTY_ID, sanitizeDifficultyId, type DifficultyId } from "./Difficulty";

export interface ContinuousProgress {
  readonly totalCaptures: number;
  readonly totalScore: number;
  readonly capturedByType: Readonly<Record<ImplementedGhostId, number>>;
  readonly weaponUses: Readonly<Record<WeaponId | "bottle", number>>;
  readonly campaignLevel:number;
  readonly completedGhosts:readonly ImplementedGhostId[];
  readonly playerHealth:number;
  readonly campaignStartedAt:number;
  readonly damageTaken:number;
  readonly correctBaitAttempts:number;
  readonly missedAttacks:number;
  readonly difficultyId:DifficultyId;
}

export interface GamePreferences {
  quality: "low" | "medium" | "high";
  masterVolume: number;
  ambienceVolume: number;
  sfxVolume: number;
  muted: boolean;
  mouseSensitivity: number;
  touchSensitivity: number;
  invertY: boolean;
}

const PROGRESS_KEY = "penangkap-hantu.progress.v1";
const PREFS_KEY = "penangkap-hantu.preferences.v1";

export const DEFAULT_GAME_PREFERENCES: Readonly<GamePreferences> = {
  quality: "medium",
  masterVolume: 0.7,
  ambienceVolume: 1,
  sfxVolume: 0.8,
  muted: false,
  mouseSensitivity: 0.0022,
  touchSensitivity: 0.004,
  invertY: false,
};

const finiteClamp = (value: unknown, fallback: number, minimum: number, maximum: number): number => {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? Math.max(minimum, Math.min(maximum, numeric)) : fallback;
};

export function sanitizeGamePreferences(value: Partial<GamePreferences> | undefined): GamePreferences {
  const quality = value?.quality === "low" || value?.quality === "high" ? value.quality : "medium";
  return {
    quality,
    masterVolume: finiteClamp(value?.masterVolume, DEFAULT_GAME_PREFERENCES.masterVolume, 0, 1),
    ambienceVolume: finiteClamp(value?.ambienceVolume, DEFAULT_GAME_PREFERENCES.ambienceVolume, 0, 1),
    sfxVolume: finiteClamp(value?.sfxVolume, DEFAULT_GAME_PREFERENCES.sfxVolume, 0, 1),
    muted: value?.muted === true,
    mouseSensitivity: finiteClamp(value?.mouseSensitivity, DEFAULT_GAME_PREFERENCES.mouseSensitivity, 0.0008, 0.006),
    touchSensitivity: finiteClamp(value?.touchSensitivity, DEFAULT_GAME_PREFERENCES.touchSensitivity, 0.0015, 0.01),
    invertY: value?.invertY === true,
  };
}

export class ProgressManager {
  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem"> = localStorage) {}

  load(): ContinuousProgress {
    try {
      const value = JSON.parse(this.storage.getItem(PROGRESS_KEY) ?? "{}") as Partial<ContinuousProgress>;
      const completed=(Array.isArray(value.completedGhosts)?value.completedGhosts:[]).filter((id):id is ImplementedGhostId=>["toyol","polong","pocong","penanggal","pontianak"].includes(String(id)));
      return { totalCaptures: value.totalCaptures ?? 0, totalScore: value.totalScore ?? 0, capturedByType: { toyol: 0, polong: 0, pocong:0,penanggal:0,pontianak: 0, ...value.capturedByType }, weaponUses: { slingshot: 0, "water-balloon": 0, "banana-peel": 0,"mosquito-spray":0, nail: 0, bottle: 0, ...value.weaponUses },campaignLevel:finiteClamp(value.campaignLevel,1,1,5),completedGhosts:completed,playerHealth:finiteClamp(value.playerHealth,100,0,100),campaignStartedAt:finiteClamp(value.campaignStartedAt,Date.now(),0,Number.MAX_SAFE_INTEGER),damageTaken:finiteClamp(value.damageTaken,0,0,Number.MAX_SAFE_INTEGER),correctBaitAttempts:finiteClamp(value.correctBaitAttempts,0,0,Number.MAX_SAFE_INTEGER),missedAttacks:finiteClamp(value.missedAttacks,0,0,Number.MAX_SAFE_INTEGER),difficultyId:sanitizeDifficultyId(value.difficultyId) };
    } catch {
      return { totalCaptures: 0, totalScore: 0, capturedByType: { toyol: 0, polong: 0,pocong:0,penanggal:0, pontianak: 0 }, weaponUses: { slingshot: 0, "water-balloon": 0, "banana-peel": 0,"mosquito-spray":0, nail: 0, bottle: 0 },campaignLevel:1,completedGhosts:[],playerHealth:100,campaignStartedAt:Date.now(),damageTaken:0,correctBaitAttempts:0,missedAttacks:0,difficultyId:DEFAULT_DIFFICULTY_ID };
    }
  }

  save(result: ContinuousProgress): void {
    this.storage.setItem(PROGRESS_KEY, JSON.stringify(result));
  }
  resetCampaign(difficultyId:DifficultyId=DEFAULT_DIFFICULTY_ID):ContinuousProgress{const fresh:ContinuousProgress={totalCaptures:0,totalScore:0,capturedByType:{toyol:0,polong:0,pocong:0,penanggal:0,pontianak:0},weaponUses:{slingshot:0,"water-balloon":0,"banana-peel":0,"mosquito-spray":0,nail:0,bottle:0},campaignLevel:1,completedGhosts:[],playerHealth:100,campaignStartedAt:Date.now(),damageTaken:0,correctBaitAttempts:0,missedAttacks:0,difficultyId:sanitizeDifficultyId(difficultyId)};this.save(fresh);return fresh;}

  loadPreferences(): GamePreferences {
    try {
      return sanitizeGamePreferences(JSON.parse(this.storage.getItem(PREFS_KEY) ?? "{}") as Partial<GamePreferences>);
    } catch { return { ...DEFAULT_GAME_PREFERENCES }; }
  }

  savePreferences(preferences: GamePreferences): void {
    this.storage.setItem(PREFS_KEY, JSON.stringify(sanitizeGamePreferences(preferences)));
  }
}
